
import querystring from 'querystring';

import { FormData } from 'window';

import _ from 'lodash';
import Q from 'q';

import { Error } from 'error';


function overJSON (fn) {
	return function (response) {
		return (
			response.bodyUsed
				? Q(response.responseJSON)
				: response.json().then(function (json) {
					response.responseJSON = json;
					return json;
				})
		)
			.then(function (json) {
				return fn(json, response);
			});
	};
}

function overBody (fn) {
	return function (options) {
		return _.set(_.cloneDeep(options), 'body', fn(options.body));
	};
}

function error (message, props) {
	return _.extend(new Error(message), props);
}


class CallbackMutex {
	// Ensures that only one wrapped callback is called and at most once
	constructor () {
		this._called = false;
	}
	wrap (fn) {
		const that = this;
		return function (...args) {
			if (that._called) {
				return undefined;
			}
			that._called = true;
			return fn.apply(this, args);
		};
	}
}

const getErrorFromHttpResponse = function (response) {
	return Q.Promise(function (resolve, reject) {
		if (response.status >= 200 && response.status < 300) {
			reject(new Error('Response HTTP status code is 2xx Success, no HTTP error here'));
		} else {
			const e = new Error(response.statusText);
			e.name = 'HttpError';
			e.response = response;
			resolve(e);
		}
	});
};

const getErrorFromJSON = overJSON(function (json, response) {
	return Q.Promise(function (resolve, reject) {
		if (!json.Error) {
			reject(new Error('Response JSON does not contain "Error" property, and that\' ok'));
		} else {
			const e = new Error(json.Error, {
				name: json.ErrorType || 'AnonymousException',
				code: json.ErrorCode,
				userFriendlyMessages: _.isEmpty(json.ErrorMessagesForClient)
					? null
					: json.ErrorMessagesForClient,
				correlationIds: _.isEmpty(json.ErrorCorrelationIds)
					? null
					: json.ErrorCorrelationIds,
				response,
			});
			resolve(e);
		}
	});
});


const util = {
	error: error,

	over: {
		json: overJSON,
		body: overBody,
	},

	filter: {
		httpError: function (response) {
			return getErrorFromHttpResponse(response)
				.then((e) => {
					// error extracted successfuly
					throw e;
				}, () => {
					// could not extract error
					return response;
				});
		},

		jsonEncodedError: function (response) {
			return getErrorFromJSON(response)
				.then((e) => {
					// error extracted successfuly
					throw e;
				}, () => {
					// could not extract error
					return response;
				});
		},

		successStatus: overJSON(function (json, response) {
			return Q.Promise(function (resolve) {
				if (json.Status === 'Success') {
					resolve(response);
				} else {
					const e = new Error(json.Status);
					e.name = 'UnexpectedStatus';
					e.response = response;
					throw e;
				}
			});
		}),

		successTrue: overJSON(function (json, response) {
			return Q.Promise(function (resolve) {
				if (json.success === true) {
					resolve(response);
				} else {
					const e = new Error('SuccessFalse');
					e.name = 'UnexpectedSuccessValue';
					e.response = response;
					throw e;
				}
			});
		}),

		authenticated: overJSON(function (json, response) {
			return Q.Promise(function (resolve) {
				if (json.IsAuthenticated
					|| json.Result === 'Authenticated'
					|| json.Result === 'AlreadyAuthenticated'
					|| json.Status === 'Success'
				   ) {
					resolve(response);
				} else {
					const e = new Error(json.Result || 'NotAuthenticated');
					e.response = response;
					throw e;
				}
			});
		}),
	},

	options: {
		body: {
			json: overBody(JSON.stringify),
			form: overBody(querystring.stringify),
			formData: overBody(function (body) {
				if (body instanceof FormData) {
					return body;
				}
				// FIXME?: nested object support
				const formData = new FormData();
				_.map(body, function (v, k) {
					formData.append(k, v);
				});
				return formData;
			}),
		},
	},

	method: {
		async: function (options) {
			// Creates an async method

			options = _.defaultsDeep({}, options, {
				// A server method that starts the task
				begin: undefined,

				// A method that decides what to do after `begin` resolved
				// Called with arguments: (response, { resolve, reject, continue })
				// Must call back `resolve`, `reject` or `continue`.
				// `continue` takes query string that is then passed to the `end` method.
				afterBegin: undefined,

				// A server method that checks if the task is done
				check: undefined,

				// A method that decides what to do after `check` resolved.
				// Analogous to afterBegin
				afterCheck: undefined,

				// How long to wait between `check` attempts
				interval: undefined,
			});

			return (...beginArgs) => {
				return options.begin(...beginArgs).then((response) => {
					const dfr = Q.defer();

					function waitAndCheck (query) {
						Q.delay(options.interval).then(() => {
							return options.check({ query }).then((response) => {
								const mutex = new CallbackMutex;

								try {
									options.afterCheck(response, {
										resolve: mutex.wrap(dfr.resolve),
										reject: mutex.wrap(dfr.reject),
										continue: mutex.wrap(_.partial(waitAndCheck, query)),
									});
								} catch (e) {
									dfr.reject(e);
								}
							});
						}).catch(dfr.reject);
					}

					const mutex = new CallbackMutex;

					try {
						options.afterBegin(response, {
							resolve: mutex.wrap(dfr.resolve),
							reject: mutex.wrap(dfr.reject),
							continue: mutex.wrap(waitAndCheck),
						});
					} catch (e) {
						dfr.reject(e);
					}

					return dfr.promise;
				});
			};
		},
	},
};

module.exports = util;
